import React from "react";
import { useNavigate } from "react-router-dom";
import NavHeader from "@/components/NavHeader";
import { useAuth } from "@/context/AuthContext";
import { Dices, ShieldAlert, Zap, TerminalSquare, Instagram } from "lucide-react";

export default function Landing() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen scanlines relative overflow-hidden">
      <NavHeader />

      <section className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 relative">
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="tape">DOOMED FUTURE</span>
              <span className="tape tape-magenta">TTRPG COMPANION</span>
              <span className="tape tape-cyan">UNOFFICIAL FAN TOOL</span>
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.85]">
              <span className="text-[color:var(--accent-yellow)]">CY_</span>
              <span className="glitch" data-text="BORG">BORG</span>
              <br />
              <span className="text-[color:var(--text-primary)]">CHARACTER</span>
              <br />
              <span className="text-[color:var(--accent-magenta)]">TERMINAL.</span>
            </h1>
            <p className="mt-6 max-w-2xl font-mono text-sm sm:text-base text-[color:var(--text-secondary)] leading-relaxed">
              Build, gut, and log your doomed punks. Full CY_BORG sheet with a
              kinetic dice terminal wired to every stat check. Play offline in your
              browser, or sync to the cloud with one sign-in.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="cta-enter-local"
                onClick={() => navigate("/dashboard")}
                className="cy-btn cy-btn-solid-yellow"
              >
                <TerminalSquare size={18} className="mr-2" /> ENTER LOCAL MODE
              </button>
              {!user && (
                <button
                  data-testid="cta-google-signin"
                  onClick={login}
                  className="cy-btn cy-btn-cyan"
                >
                  <Zap size={18} className="mr-2" /> SIGN IN & SYNC
                </button>
              )}
              {user && (
                <button
                  data-testid="cta-dashboard"
                  onClick={() => navigate("/dashboard")}
                  className="cy-btn cy-btn-magenta"
                >
                  MY PUNKS →
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="panel panel-magenta p-5 sm:p-6 relative">
              <div className="absolute -top-3 -left-2 tape tape-magenta">// ADVISORY</div>
              <div className="flex items-start gap-3 mb-4">
                <ShieldAlert className="text-[color:var(--accent-magenta)]" />
                <div className="font-display uppercase tracking-tight text-lg leading-tight">
                  Everything ends. Roll bad.
                </div>
              </div>
              <ul className="font-mono text-sm space-y-2 text-[color:var(--text-secondary)]">
                <li>▸ Full character sheet, all fields</li>
                <li>▸ D4 → D100, D66, modifiers, adv/dis</li>
                <li>▸ Stat-linked one-tap checks</li>
                <li>▸ Point-buy soft limits w/ hazard alerts</li>
                <li>▸ Local save OR cloud sync</li>
                <li>▸ Persistent roll history</li>
              </ul>
              <div className="mt-5 pt-5 border-t-2 border-dashed border-[color:var(--border-hard)] flex items-center gap-2">
                <Dices className="text-[color:var(--accent-yellow)]" />
                <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
                  Tap the dice on any screen.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-16">
        <div className="hazard-tape h-3 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { t: "01 // Roll or Assemble", d: "Enter every CY_BORG stat manually. Soft limits warn you before your GM does." },
            { t: "02 // Track the Damage", d: "HP, sats, wounds, glitches, cybertech — logged on one gritty page." },
            { t: "03 // Roll With Impunity", d: "Kinetic dice terminal always docked. Every roll logged forever." },
          ].map((f, i) => (
            <div key={i} className="panel p-5">
              <div className="font-display font-black uppercase text-[color:var(--accent-yellow)] tracking-tight text-lg">{f.t}</div>
              <div className="font-mono text-sm text-[color:var(--text-secondary)] mt-2">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 border-t-2 border-[color:var(--border-hard)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
          UNOFFICIAL FAN COMPANION · NO AFFILIATION WITH FREE LEAGUE OR STOCKHOLM KARTELL · GOOD LUCK, YOU&apos;LL NEED IT.
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <a
            data-testid="instagram-link"
            href="https://www.instagram.com/frinkontheink?stkn=MTNibWxzcm5penh4NA=="
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow @frinkontheink on Instagram"
            className="cy-btn cy-btn-magenta text-xs whitespace-nowrap"
          >
            <Instagram size={14} className="mr-1" /> @FRINKONTHEINK
          </a>
          <a
            data-testid="groupfinder-badge"
            href="https://groupfinder.gg/library/cy-borg-character-creator"
            target="_blank"
            rel="noopener"
            aria-label="Groupfinder library — Collection of TTRPG resources"
            className="inline-block"
            style={{ display: "inline-block" }}
          >
            <img
              src="https://groupfinder.gg/images/badges/gf-badge-light.svg"
              alt="Groupfinder library - Collection of TTRPG resources"
              width="200"
              height="55"
              style={{ width: "200px", height: "55px", border: 0, display: "block" }}
            />
          </a>
        </div>
      </footer>
    </div>
  );
}
